import { Dialog } from '@headlessui/react';
import { CloudArrowUpIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';

import { uploadGoldenDemand } from '../../services/goldenDemandService';
import { Button } from '../ui/catalyst/button';
import { Input } from '../ui/catalyst/input';
import { Textarea } from '../ui/catalyst/textarea';
import { Label as UILabel } from '../ui/label';

interface GoldenDemandUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUploadComplete: () => void;
}

export const GoldenDemandUploadModal = ({
  isOpen,
  onClose,
  onUploadComplete,
}: GoldenDemandUploadModalProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile.name.match(/\.(docx|pdf|txt)$/i)) {
        setFile(droppedFile);
      } else {
        toast.error('Please upload a DOCX, PDF, or text file');
      }
    }
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      if (selectedFile.name.match(/\.(docx|pdf|txt)$/i)) {
        setFile(selectedFile);
      } else {
        toast.error('Please upload a DOCX, PDF, or text file');
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!file || !name) {
      toast.error('Please provide a file and name');
      return;
    }

    setUploading(true);

    try {
      await uploadGoldenDemand({
        file,
        name,
        description,
      });

      onUploadComplete();

      // Reset form
      setFile(null);
      setName('');
      setDescription('');
    } catch (error) {
      toast.error('Failed to upload gold standard');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex justify-between items-start mb-4">
              <Dialog.Title className="text-lg font-semibold">
                Upload Gold Standard Demand
              </Dialog.Title>
              <button
                type="button"
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* File Upload */}
              <div>
                <UILabel htmlFor="file">Document File</UILabel>
                <div
                  className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md ${
                    dragActive
                      ? 'border-blue-400 bg-blue-50'
                      : 'border-gray-300'
                  }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  <div className="space-y-1 text-center">
                    <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <div className="flex text-sm text-gray-600">
                      <label
                        htmlFor="file-upload"
                        className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500"
                      >
                        <span>Upload a file</span>
                        <input
                          id="file-upload"
                          name="file-upload"
                          type="file"
                          className="sr-only"
                          accept=".docx,.pdf,.txt"
                          onChange={handleFileChange}
                        />
                      </label>
                      <p className="pl-1">or drag and drop</p>
                    </div>
                    <p className="text-xs text-gray-500">
                      DOCX, PDF, or text files
                    </p>
                    {file && (
                      <p className="text-sm text-green-600 mt-2">
                        Selected: {file.name}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Name */}
              <div>
                <UILabel htmlFor="name">Name *</UILabel>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Perfect Auto Accident Demand"
                  required
                />
              </div>

              {/* Description */}
              <div>
                <UILabel htmlFor="description">Description</UILabel>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe this gold standard document"
                  rows={3}
                />
              </div>

              {/* Submit */}
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" plain onClick={onClose}>
                  Cancel
                </Button>
                <Button type="submit" disabled={uploading || !file || !name}>
                  {uploading ? 'Uploading...' : 'Upload Gold Standard'}
                </Button>
              </div>
            </form>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};
